import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, usePathname } from 'next/navigation';
import {
  Configure,
  Hits,
  InfiniteHits,
  Pagination,
  useClearRefinements,
  useCurrentRefinements,
  useHits,
  usePagination,
  useSortBy,
} from 'react-instantsearch-hooks-web';
import CurrentRefinements from './components/current-refinements';
import DesktopFacets from './components/desktop-facets';
import MobileFacets from './components/mobile-facets';
import DesktopSortFacets from './components/desktop-facets/sort-facets';
import ViewToggleButton from '@/lib/tastics/project-builder-page/components/toggleView';
import { FacetConfiguration } from './types';
import ProductTile from '../product-tile';
import { mapProduct } from '@/helpers/algolia/project-builder/map-products';
import { useLocalizedIndex } from '@/providers/localized-index';
import { useFormat } from '@/helpers/hooks/useFormat';
import useMediaQuery from '@/helpers/hooks/useMediaQuery';
import { mediumDesktop, semiLargeDesktop } from '@/helpers/utils/screensizes';
import AddProductToProject from '../../add-prod-to-project';
import AccordionBtn from '@/components/atoms/project-builder/accordion';
import { isTohidePriceData } from '../product-details/components/internalBasisRangePrice';
import Spinner from '@/components/organisms/spinner';
import eventBus from '@/utils/eventbus';

interface Props {
  noContentSuggestions?: any;
  slug?: string;
  searchQuery?: string;
  categories: any;
  facetsConfiguration: Record<string, FacetConfiguration>;
  suggestions?: any;
  isBimLibraryPage?: any;
  projectForm?: any;
  viewMode?: 'grid' | 'list';
  defaultFilters?: any;
  toggleView?: () => void;
}
interface NoResultProps {
  fallback: ReactNode;
}
function NoResultsBoundary({ children, fallback }: React.PropsWithChildren<NoResultProps>) {
  const { results } = useHits();
  if (results && !results?.__isArtificial && results?.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }
  return children;
}
function NoResults({ results, suggestions, searchQuery }: any) {
  const { formatMessage } = useFormat({ name: 'common' });

  return (
    <div className="relative min-h-[25vh]">
      {searchQuery && results?.nbHits === 0 ? (
        <div className="m-auto max-w-[900px] text-center lg:!text-left">
          <p className={`results-zero no-result-container mb-2`}>No products found</p>
          <div className="d-inline">
            {formatMessage({
              id: 'no.resultfound.query',
              defaultMessage: 'No search results are found for your query:',
            })}
            <span className="font-bold"> &quot;{searchQuery ? searchQuery : ''}&quot;</span>.
          </div>
        </div>
      ) : (
        <p className={`${results?.nbHits === 0 && 'results-zero'} no-result-container`}>No products found</p>
      )}
    </div>
  );
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const ProductListAlgolia: React.FC<Props> = ({
  searchQuery,
  facetsConfiguration,
  suggestions,
  isBimLibraryPage,
  projectForm,
  viewMode,
  defaultFilters,
  toggleView,
}) => {
  const ConfigureAny = Configure as any;
  const [isMediumDesktop] = useMediaQuery(mediumDesktop);
  const [issemiLargeDesktop] = useMediaQuery(semiLargeDesktop);
  const { locale } = useParams();
  const pathname = usePathname();
  const isProjectBuilderPath = !!pathname && pathname.toLowerCase().includes('project-builder');
  const plpBgClass = isProjectBuilderPath ? 'bg-white' : 'bg-[#F3EDE9]';
  const { refine } = usePagination();
  const { refine: clearAllRefinements } = useClearRefinements();
  const { indexName: productsIndex } = useLocalizedIndex({ type: 'products' });
  const [spinner, setSpinner] = useState(true);
  const { refine: clearSortRefinement } = useSortBy({
    items: [
      {
        label: 'New',
        value: productsIndex,
      },
      {
        label: 'Name A-Z',
        value: productsIndex.replace('_created_desc', '') + '_name_asc',
      },
      {
        label: 'Name Z-A',
        value: productsIndex.replace('_created_desc', '') + '_name_desc',
      },
    ],
  });
  const { items } = useCurrentRefinements();
  const { hits, results } = useHits();
  const hitsLength = hits.length;
  const totalHits = results ? results.nbHits : 0;
  const [openAddToProject, setOpenAddToProject] = useState<boolean>(false);
  const [productToAdd, setProductToAdd] = useState<any>(null);
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());
  const filterTitle = isMediumDesktop
    ? (!isProjectBuilderPath ? 'Filters' : 'Products')
    : 'Filter Product';
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 400);

  useEffect(() => {
    if (results && !results.__isArtificial) {
      setSpinner(false);
    }
  }, [results]);

  useEffect(() => {
    eventBus.emit('totalProducts', totalHits);
  }, [totalHits]);

  useEffect(() => {
    refine(0);
    clearAllRefinements();
    clearSortRefinement(productsIndex);
  }, [searchQuery]);

  const handleChange = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target instanceof HTMLAnchorElement) {
      const element = document.getElementById('plp-container');
      if (element) {
        window.scrollTo({
          top: element.offsetTop,
          behavior: 'smooth',
        });
      }
    }
  };
  const handleCancelAddProduct = () => {
    setOpenAddToProject(false);
    setProductToAdd(null);
  };
  const handleAddToProject = useCallback((variant: any, p: any) => {
    setOpenAddToProject(true);
    setProductToAdd({ name: p.name, sku: variant?.sku });
  }, []);

  const handleSelectProduct = useCallback((sku: string) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      next.has(sku) ? next.delete(sku) : next.add(sku);
      return new Set(next);
    });
  }, []);

  const getIsCheckedProduct = useCallback(
    (sku: string) => selectedSkus.has(sku),
    [selectedSkus],
  );

  const products = useMemo(() => hits.map(hit => mapProduct(hit, locale, searchQuery)), [hits, locale, searchQuery]);
  const showForInfoMsg = useMemo(() => {
    return products.some(product => {
      const isGWAInternal = product?.attributesAlgolia?.['IsGWAInternal'];
      const isBuilderRange = product?.attributesAlgolia?.['builder-range']?.['en-AU']?.toUpperCase() === 'YES';
      const isLoggedInInternalUser = false;

      return isTohidePriceData({
        locale,
        isBuilderRange,
        isGWAInternal,
        isLoggedInInternalUser
      });
    });
  }, [products, locale]);

  const [stickyShowForInfoMsg, setStickyShowForInfoMsg] = useState(false);
  useEffect(() => {
    if (showForInfoMsg && !stickyShowForInfoMsg && window.innerWidth < 1280) {
      setStickyShowForInfoMsg(true);
    }
  }, [showForInfoMsg, stickyShowForInfoMsg]);

  const HitWrapper = ({
    hit,
    handleAddToProject,
    isBimLibraryPage,
    handleSelectProduct,
    getIsCheckedProduct,
    viewMode,
    showForInfoMsg
  }: {
    hit: any;
    handleAddToProject: (variant: any, p: any) => void;
    isBimLibraryPage: boolean;
    handleSelectProduct: (sku: string) => void;
    getIsCheckedProduct: (sku: string) => boolean;
    viewMode?: string;
    showForInfoMsg?: boolean
  }) => {
    const { locale } = useParams();
    const product = useMemo(() => mapProduct(hit, locale, searchQuery), [hit, locale, searchQuery]);

    return (
      <ProductTile
        images={hit?.images}
        product={product}
        isSearchResult={!!searchQuery}
        isCategoryPage={true}
        onAddToProject={(variant) => handleAddToProject(variant, product)}
        isBimLibraryPage={isBimLibraryPage}
        getIsCheckedProduct={() => getIsCheckedProduct(product.variants[0]?.sku)}
        handleSelectProduct={() => handleSelectProduct(product.variants[0]?.sku)}
        viewMode={viewMode}
        showForInfoMsg={showForInfoMsg}
      />
    );
  };

  const memoizedHitComponent = useCallback(
    (hitProps: { hit: any; }) => (
      <HitWrapper
        {...hitProps}
        handleAddToProject={handleAddToProject}
        isBimLibraryPage={isBimLibraryPage}
        handleSelectProduct={handleSelectProduct}
        getIsCheckedProduct={getIsCheckedProduct}
        viewMode={viewMode}
        showForInfoMsg={window.innerWidth < 1280 ? stickyShowForInfoMsg : showForInfoMsg}
      />
    ),
    [handleAddToProject, isBimLibraryPage, handleSelectProduct, getIsCheckedProduct, viewMode, searchQuery, showForInfoMsg, stickyShowForInfoMsg],
  );
  let filterValue = defaultFilters ?? "";
const textSize = filterTitle === 'Filters' ? 'text-[32px]' : 'text-[18px]';
  return (
    <>
      <div id="plp-container" className={`${searchQuery && results?.nbHits === 0 ? 'lg:mt-[40px]' : ''}`}>
        <ConfigureAny
          query={debouncedSearchQuery ?? ''}
          filters={filterValue}
          hitsPerPage={12}
          facets={['*']}
          attributesToHighlight={[]}
          attributesToSnippet={[]}
          attributesToRetrieve={[
            'name',
            'slug',
            'sku',
            'categories',
            'productId',
            'prices',
            'images',
            'attributes',
            'searchableAttributes.IsNew',
            'searchableAttributes.builder-range',
            'searchableAttributes.range',
            'variants.attributes.range',
            'variants.attributes.exclusive-product-NZ',
            'variants.id',
            'variants.key',
            'variants.sku',
          ]}
          maxValuesPerFacet={999}
        />
        <div
          className={`${results?.nbHits === 0 && items?.length === 0 ? '' : 'grid items-start gap-4 xl:flex xl:flex-row'
            } pb-0`}
        >
          {searchQuery && results?.nbHits == 0 ? (
            <></>
          ) : (
            <div className={`desktop-facets hidden min-w-[280px] rounded-md rounded-b-none ${plpBgClass} text-black xl:block xl:min-w-[280px] z-10`}>
              <div className="flex flex-col items-start">
                {!isProjectBuilderPath && isMediumDesktop ? (
                  <div className={`w-[280px] min-w-[280px] lg:min-w-[280px] 2xl:min-w-[280px] ${plpBgClass}`}>
                    <DesktopFacets
                      facetsConfiguration={facetsConfiguration}
                      title={filterTitle}
                      isProjectBuilderPath={isProjectBuilderPath}
                      isMediumDesktop={isMediumDesktop}
                    />
                  </div>
                ) : (
                  <AccordionBtn
                    key={`accordion-${isMediumDesktop}`}
                    closedSectionTitle={filterTitle}
                    className={`min-w-[280px] text-[18px] 2xl:min-w-[280px] ${plpBgClass}`}
                    buttonClassName={`!py-[8px] xl:!px-4 ${textSize} 2xl:text-[18px] font-normal text-[#090909] text-left pl-[10px]`}
                    panelClassName="text-[18px] 2xl:text-[18px] text-[#090909] font-normal"
                    iconClassName="w-[18px] text-primary-black"
                    defaultOpen={isMediumDesktop}
                  >
                    <div className="flex flex-col items-start">
                      <DesktopFacets facetsConfiguration={facetsConfiguration} />
                    </div>
                  </AccordionBtn>
                )}
              </div>
            </div>
          )}
          <div className={`w-full ${plpBgClass} xl:mb-5 `}>
            {!isMediumDesktop && (
              <div className="mb-4">
                <MobileFacets
                  facetsConfiguration={facetsConfiguration}
                  isBimLibraryPage={isBimLibraryPage}
                  viewMode={viewMode}
                  toggleView={toggleView}
                />
              </div>
            )}
            {!isProjectBuilderPath && isMediumDesktop && (
              <div className="flex items-center justify-end gap-3">
                <DesktopSortFacets />
                {toggleView && viewMode && (
                  <ViewToggleButton viewMode={viewMode} toggleView={toggleView} />
                )}
              </div>
            )}
            <div className="mt-4">
              <CurrentRefinements />
            </div>
            {isMediumDesktop ? (
              <>
                <NoResultsBoundary
                  fallback={<NoResults results={results} suggestions={suggestions} searchQuery={searchQuery} />}
                >
                  {viewMode === 'list' && (
  <div className="hidden lg:grid grid-cols-2 gap-2 px-2 mt-4">
    <div className="text-[16px] font-semibold text-[#090909]">
      Product
    </div>

    <div className="grid items-center gap-1 lg:grid-cols-4 text-[16px] font-semibold text-[#090909]">
      <div className="pl-1">Code</div>

      <div className="pl-1">
        <div>RRP</div>
        <div>(inc. GST)</div>
      </div>

      <div className="pl-1">Actions</div>

      <div className="text-center">
  <div>Saved</div>
  <div>Products</div>
</div>
    </div>
  </div>
)}
                  <Hits
                    hitComponent={memoizedHitComponent}
                    classNames={{
                      root: searchQuery ? 'pt-[12px]' : 'pt-[12px] ',
                      list: `${viewMode != 'list' ? 'grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 ' : ''}`,
                      item: `${
                        viewMode === 'list'
                          ? !isProjectBuilderPath
                            ? 'mb-[10px] bg-white'
                            : 'mb-[10px] even:bg-[#E5E7EB] odd:bg-white'
                          : ''
                        }`,
                    }}
                  />

                  <Pagination
                    showFirst={false}
                    showLast={false}
                    padding={isMediumDesktop ? 3 : 1}
                    onClick={(event) => handleChange(event)}
                    classNames={{
                      root: 'flex justify-end !mt-[48px] !mb-[48px]',
                      list: 'flex',
                      selectedItem: 'border !border-[#2B2C39] bg-[#DDDDDD] !text-[#174F1C]',
                      item: 'py-[10px] border !border-[#DDDDDD] text-[#808080] text-[14px]',
                      link: 'px-[18px] py-[10px]',
                      disabledItem: 'hidden',
                    }}
                  />
                </NoResultsBoundary>
              </>
            ) : (
              <NoResultsBoundary
                fallback={<NoResults results={results} suggestions={suggestions} searchQuery={searchQuery} />}
              >
                <InfiniteHits
                  showPrevious={false}
                  hitComponent={memoizedHitComponent}
                  classNames={{
                    root: searchQuery ? 'pt-[32px]' : 'pt-[32px]',
                    list: `${viewMode != 'list' ? 'grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4' : ''}`,
                    item: `${
                      viewMode === 'list'
                        ? !isProjectBuilderPath
                          ? 'mb-[10px] bg-white'
                          : 'mb-[10px] even:bg-[#E5E7EB] odd:bg-white'
                        : ''
                      }`,
                    loadMore:
                      totalHits > 12 && hitsLength >= 12
                        ? 'mt-[48px] border-1 rounded-none border border-[#2B2C39] bg-[#2B2C39] shadow-sm text-[16px] text-white px-[18px] py-[10px] md:px-[32px] md:py-[18px] w-full'
                        : '!hidden',
                  }}
                  translations={{
                    showMoreButtonText: 'Show more',
                  }}
                />
              </NoResultsBoundary>
            )}
          </div>
        </div>
      </div>
      {openAddToProject && (
        <AddProductToProject
          open={openAddToProject}
          onCancel={handleCancelAddProduct}
          productToAdd={productToAdd}
          projectForm={projectForm}
          fromProjectBuilder={true}
        />
      )}
      {spinner && <Spinner />}
      {spinner && <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>}
    </>
  );
};
export default ProductListAlgolia;
